import axios, {
  type AxiosError,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from 'axios'
import { useMemo, useRef } from 'react'
import { useAuth } from 'react-oidc-context'
import i18n from '@/i18n'
import { type ErrorCode } from '@/lib/constants/error-codes'
import { getEnv } from '@/lib/config/runtime-env'
import { renewSession } from '@/lib/auth/renew-session'
import { isDeadSessionError } from '@/features/authentication/is-dead-session-error'
import { useApiErrors } from './use-api-errors'

const UNAUTHORIZED_STATUS_CODE = 401

/** Marks a request that has already consumed its one renewal retry. */
type RetryableConfig = InternalAxiosRequestConfig & {
  _renewRetried?: boolean
}

const useClientApi = ({
  excludedErrorCodesFromToast,
}: {
  excludedErrorCodesFromToast?: readonly ErrorCode[]
} = {}) => {
  const baseURL = getEnv().BASEURL
  const { user, removeUser } = useAuth()
  const { showErrorToast } = useApiErrors()

  // Keep the latest token in a ref so the request interceptor always reads the
  // current value — including a token the host silently renewed after this
  // instance was created. (Setting Authorization on axios.create freezes it.)
  const tokenRef = useRef<string | undefined>(user?.access_token)
  tokenRef.current = user?.access_token

  const API: AxiosInstance = useMemo(() => {
    const instance = axios.create({
      baseURL,
      headers: {
        Accept: 'application/json',
        'Content-type': 'application/json',
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
        Expires: '0',
      },
    })

    instance.interceptors.request.use((config) => {
      // Read the locale at REQUEST time (i18n is the single source of truth) so a
      // language switch makes every refetch carry the new Accept-Language. Setting
      // it on axios.create would freeze it at the instance's creation locale.
      config.headers.set('Accept-Language', i18n.language)
      const token = tokenRef.current
      // A replay sets the renewed token on the config itself; do not overwrite it
      // with stale React state. tokenRef still holds the token that just 401'd
      // until react-oidc-context re-renders this hook.
      if (token && !config.headers.has('Authorization')) {
        config.headers.set('Authorization', `Bearer ${token}`)
      }
      return config
    })

    instance.interceptors.response.use(
      (response) => response,
      async (error: AxiosError<{ errorCode?: string }>) => {
        const errorCode = error.response?.data?.errorCode

        // Don't show toast for canceled/aborted requests
        if (
          axios.isCancel(error) ||
          error.code === 'ERR_CANCELED' ||
          error.message === 'canceled' ||
          error.message === 'aborted'
        ) {
          return Promise.reject(error)
        }

        // A 401 is usually just an expired access token — at a 10-minute
        // lifetime that happens several times per session. Renew and replay
        // before concluding anything.
        if (error.response?.status === UNAUTHORIZED_STATUS_CODE) {
          const config = error.config as RetryableConfig | undefined

          // The request went out unauthenticated: a 401 here means the endpoint
          // wants a login, not a fresher token.
          if (!tokenRef.current) {
            return Promise.reject(error)
          }

          // One recovery attempt per request. A second 401, carrying a token we
          // had just minted, means the session is genuinely unusable.
          if (!config || config._renewRetried) {
            void removeUser()
            return Promise.reject(error)
          }
          config._renewRetried = true

          let token: string
          try {
            token = await renewSession()
          } catch (renewalError) {
            // Only invalid_grant proves the refresh token is dead. A network
            // blip, a timeout or a 5xx says nothing about the session, and
            // evicting the user over one is what turned every hiccup into a
            // logout. Fail just this request and let normal handling take it.
            if (isDeadSessionError(renewalError)) {
              void removeUser()
            }
            return Promise.reject(error)
          }

          tokenRef.current = token
          config.headers.set('Authorization', `Bearer ${token}`)
          return instance.request(config)
        }

        if (
          errorCode &&
          excludedErrorCodesFromToast?.includes(errorCode as ErrorCode)
        ) {
          return Promise.reject(error)
        }
        // Handle API errors with the utility function
        showErrorToast({ error })
        return Promise.reject(error)
      }
    )

    return instance
  }, [baseURL, removeUser, showErrorToast, excludedErrorCodesFromToast])

  return { API }
}

export default useClientApi
